<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import CreateNewCompanyDialog from 'dashboard/components-next/Companies/CompaniesForm/CreateNewCompanyDialog.vue';

const props = defineProps({
  initialContactId: {
    type: Number,
    default: null,
  },
  initialCompanyId: {
    type: Number,
    default: null,
  },
  initialFunnelId: {
    type: [Number, String],
    default: null,
  },
  initialConversationId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['created']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const createContactDialogRef = ref(null);
const createCompanyDialogRef = ref(null);
const isSubmitting = ref(false);

// Form data
const formData = ref({
  name: '',
  amount: '',
  stage_name: '',
  probability: 10,
  expected_close_date: '',
  contact_id: props.initialContactId,
  company_id: props.initialCompanyId,
  funnel_id: props.initialFunnelId,
  conversation_id: props.initialConversationId,
  description: '',
});

// Search/filter for contacts
const contactSearch = ref('');

// Dropdowns state
const showCompanyDropdown = ref(false);
const showContactDropdown = ref(false);
const showStageDropdown = ref(false);
const showFunnelDropdown = ref(false);

// Companies, contacts and funnels from store
const companies = computed(
  () => store.getters['companies/getCompaniesList'] || []
);
const contacts = computed(() => store.getters['contacts/getContacts'] || []);
const funnels = useMapGetter('funnels/getFunnels');

// Sales funnels - funnels do tipo "sales" ou "custom" que podem ter deals
const availableFunnels = computed(() => {
  return (funnels.value || []).filter(
    f => f.funnel_type === 'sales' || f.funnel_type === 'custom'
  );
});

const selectedCompany = computed(() => {
  if (!formData.value.company_id) return null;
  return companies.value.find(c => c.id === formData.value.company_id);
});

const selectedContact = computed(() => {
  if (!formData.value.contact_id) return null;
  return contacts.value.find(c => c.id === formData.value.contact_id);
});

// Filtered contacts based on search
const filteredContacts = computed(() => {
  if (!contactSearch.value) return contacts.value;
  const search = contactSearch.value.toLowerCase();
  return contacts.value.filter(
    c =>
      c.name?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      c.phone_number?.includes(search)
  );
});

const selectedFunnel = computed(() => {
  if (!formData.value.funnel_id) return null;
  return availableFunnels.value.find(
    f => f.id === Number(formData.value.funnel_id)
  );
});

// Stages from selected funnel
const stages = computed(() => {
  if (!selectedFunnel.value?.stages) return [];
  return Object.entries(selectedFunnel.value.stages)
    .map(([key, config]) => ({
      key,
      label: config.label || key,
      color: config.color || '#718096',
      position: config.position || 0,
      probability: config.probability || 0,
    }))
    .sort((a, b) => a.position - b.position);
});

const selectedStage = computed(() => {
  if (!formData.value.stage_name) return stages.value[0] || null;
  return stages.value.find(s => s.key === formData.value.stage_name);
});

const isFormValid = computed(() => {
  return (
    formData.value.name.trim() !== '' &&
    formData.value.funnel_id &&
    formData.value.stage_name
  );
});

const selectCompany = company => {
  formData.value.company_id = company.id;
  showCompanyDropdown.value = false;
};

const selectContact = contact => {
  formData.value.contact_id = contact.id;
  showContactDropdown.value = false;
};

const selectFunnel = funnel => {
  formData.value.funnel_id = funnel.id;
  showFunnelDropdown.value = false;
  // Set default stage from funnel
  if (funnel.stages) {
    const stagesList = Object.entries(funnel.stages)
      .map(([key, config]) => ({
        key,
        position: config.position || 0,
        probability: config.probability || 0,
      }))
      .sort((a, b) => a.position - b.position);
    if (stagesList.length > 0) {
      formData.value.stage_name = stagesList[0].key;
      formData.value.probability = stagesList[0].probability || 10;
    }
  }
};

const selectStage = stage => {
  formData.value.stage_name = stage.key;
  formData.value.probability = stage.probability || formData.value.probability;
  showStageDropdown.value = false;
};

const resetForm = () => {
  formData.value = {
    name: '',
    amount: '',
    stage_name: '',
    probability: 10,
    expected_close_date: '',
    contact_id: props.initialContactId,
    company_id: props.initialCompanyId,
    funnel_id: props.initialFunnelId,
    conversation_id: props.initialConversationId,
    description: '',
  };
  contactSearch.value = '';
  // Set default stage if funnel is pre-selected
  if (props.initialFunnelId && selectedFunnel.value?.stages) {
    const stagesList = Object.entries(selectedFunnel.value.stages)
      .map(([key, config]) => ({ key, position: config.position || 0 }))
      .sort((a, b) => a.position - b.position);
    if (stagesList.length > 0) {
      formData.value.stage_name = stagesList[0].key;
    }
  }
};

const handleSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    const dealData = {
      ...formData.value,
      amount: formData.value.amount ? parseFloat(formData.value.amount) : 0,
    };

    // Remove empty string values to avoid API errors
    Object.keys(dealData).forEach(key => {
      if (dealData[key] === '' || dealData[key] === null) {
        delete dealData[key];
      }
    });

    await store.dispatch('deals/createDeal', dealData);
    useAlert(t('DEALS.CREATE.SUCCESS'));
    emit('created');
    resetForm();
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errors?.join(', ') || t('DEALS.CREATE.ERROR');
    useAlert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

const loadData = async () => {
  // Load companies and contacts if not loaded
  if (!companies.value || companies.value.length === 0) {
    store.dispatch('companies/get', { page: 1 });
  }
  if (!contacts.value || contacts.value.length === 0) {
    store.dispatch('contacts/get', { page: 1 });
  }
  // Load funnels if not loaded
  if (!funnels.value || funnels.value.length === 0) {
    await store.dispatch('funnels/get');
  }
  // Set initial funnel and stage
  if (props.initialFunnelId) {
    const funnel = availableFunnels.value.find(
      f => f.id === Number(props.initialFunnelId)
    );
    if (funnel) {
      selectFunnel(funnel);
    }
  } else if (availableFunnels.value.length > 0 && !formData.value.funnel_id) {
    // Auto-select first funnel
    selectFunnel(availableFunnels.value[0]);
  }
};

const open = async () => {
  await loadData();
  dialogRef.value?.open();
};

const closeDialog = () => {
  dialogRef.value?.close();
};

// Create Contact/Company handlers
const openCreateContactDialog = () => {
  showContactDropdown.value = false;
  createContactDialogRef.value?.dialogRef?.open();
};

const openCreateCompanyDialog = () => {
  showCompanyDropdown.value = false;
  createCompanyDialogRef.value?.dialogRef?.open();
};

const handleContactCreated = async contactData => {
  await store.dispatch('contacts/create', contactData);
  const newContact = store.getters['contacts/getContacts']?.find(
    c => c.email === contactData.email || c.name === contactData.name
  );
  if (newContact) {
    formData.value.contact_id = newContact.id;
  }
  createContactDialogRef.value?.onSuccess();
  await store.dispatch('contacts/get', { page: 1 });
};

const handleCompanyCreated = async companyData => {
  await store.dispatch('companies/create', companyData);
  const newCompany = store.getters['companies/getCompaniesList']?.find(
    c => c.name === companyData.name
  );
  if (newCompany) {
    formData.value.company_id = newCompany.id;
  }
  createCompanyDialogRef.value?.onSuccess();
  await store.dispatch('companies/get', { page: 1 });
};

// Watch for initial funnel changes
watch(
  () => props.initialFunnelId,
  newFunnelId => {
    if (newFunnelId) {
      formData.value.funnel_id = newFunnelId;
      const funnel = availableFunnels.value.find(
        f => f.id === Number(newFunnelId)
      );
      if (funnel) {
        selectFunnel(funnel);
      }
    }
  },
  { immediate: true }
);

onMounted(() => {
  loadData();
});

defineExpose({ open, dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('DEALS.CREATE.TITLE')"
    width="xl"
    @confirm="handleSubmit"
  >
    <div class="flex flex-col gap-4">
      <!-- Nome do negócio -->
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('DEALS.FORM.NAME') }}
          <span class="text-n-ruby-9">*</span>
        </label>
        <Input
          v-model="formData.name"
          type="text"
          :placeholder="t('DEALS.FORM.NAME_PLACEHOLDER')"
        />
      </div>

      <!-- Funil (Pipeline) -->
      <div class="relative">
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('DEALS.FORM.FUNNEL') }}
          <span class="text-n-ruby-9">*</span>
        </label>
        <button
          type="button"
          class="w-full px-3 py-2 text-left border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12 hover:bg-n-alpha-3"
          @click="showFunnelDropdown = !showFunnelDropdown"
        >
          {{ selectedFunnel?.name || t('DEALS.FORM.SELECT_FUNNEL') }}
        </button>
        <div
          v-if="showFunnelDropdown"
          class="absolute z-20 w-full mt-1 bg-n-solid-3 border border-n-weak rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          <button
            v-for="funnel in availableFunnels"
            :key="funnel.id"
            type="button"
            class="w-full px-3 py-2 text-left text-n-slate-12 hover:bg-n-alpha-3 first:rounded-t-lg last:rounded-b-lg"
            @click="selectFunnel(funnel)"
          >
            {{ funnel.name }}
          </button>
          <div
            v-if="availableFunnels.length === 0"
            class="px-3 py-2 text-sm text-n-slate-11 text-center"
          >
            {{ t('DEALS.FORM.NO_FUNNELS') }}
          </div>
        </div>
      </div>

      <!-- Valor e Estágio -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('DEALS.FORM.AMOUNT') }}
          </label>
          <Input
            v-model="formData.amount"
            type="number"
            :placeholder="t('DEALS.FORM.AMOUNT_PLACEHOLDER')"
          />
        </div>
        <div class="relative">
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('DEALS.FORM.STAGE') }}
            <span class="text-n-ruby-9">*</span>
          </label>
          <button
            type="button"
            class="w-full px-3 py-2 text-left border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12 hover:bg-n-alpha-3 disabled:opacity-50"
            :disabled="!selectedFunnel"
            @click="showStageDropdown = !showStageDropdown"
          >
            <span v-if="selectedStage" class="flex items-center gap-2">
              <span
                class="w-2 h-2 rounded-full"
                :style="{ backgroundColor: selectedStage.color }"
              />
              {{ selectedStage.label }}
            </span>
            <span v-else class="text-n-slate-10">
              {{ t('DEALS.FORM.SELECT_STAGE') }}
            </span>
          </button>
          <div
            v-if="showStageDropdown && stages.length > 0"
            class="absolute z-10 w-full mt-1 bg-n-solid-3 border border-n-weak rounded-lg shadow-lg"
          >
            <button
              v-for="stage in stages"
              :key="stage.key"
              type="button"
              class="w-full px-3 py-2 text-left text-n-slate-12 hover:bg-n-alpha-3 first:rounded-t-lg last:rounded-b-lg flex items-center gap-2"
              @click="selectStage(stage)"
            >
              <span
                class="w-2 h-2 rounded-full"
                :style="{ backgroundColor: stage.color }"
              />
              {{ stage.label }}
              <span class="text-n-slate-10 text-xs ml-auto">
                {{ stage.probability }}%
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Probabilidade e Data de fechamento -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('DEALS.FORM.PROBABILITY') }}
          </label>
          <div class="flex items-center gap-2">
            <Input
              v-model="formData.probability"
              type="number"
              min="0"
              max="100"
              class="flex-1"
            />
            <span class="text-n-slate-11">%</span>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('DEALS.FORM.EXPECTED_CLOSE_DATE') }}
          </label>
          <Input v-model="formData.expected_close_date" type="date" />
        </div>
      </div>

      <!-- Contato (Principal) -->
      <div class="relative">
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('DEALS.FORM.CONTACT') }}
        </label>
        <div class="flex gap-2">
          <button
            type="button"
            class="flex-1 px-3 py-2 text-left border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12 hover:bg-n-alpha-3"
            @click="showContactDropdown = !showContactDropdown"
          >
            <span v-if="selectedContact" class="flex items-center gap-2">
              <span class="i-lucide-user size-4 text-n-slate-11" />
              {{ selectedContact.name }}
              <span
                v-if="selectedContact.email"
                class="text-n-slate-10 text-xs"
              >
                ({{ selectedContact.email }})
              </span>
            </span>
            <span v-else class="text-n-slate-10">
              {{ t('DEALS.FORM.SELECT_CONTACT') }}
            </span>
          </button>
          <button
            type="button"
            class="flex items-center justify-center px-3 py-2 border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-11 hover:bg-n-alpha-3 hover:text-n-slate-12"
            :title="t('DEALS.FORM.CREATE_CONTACT')"
            @click="openCreateContactDialog"
          >
            <span class="i-lucide-plus size-4" />
          </button>
        </div>
        <div
          v-if="showContactDropdown"
          class="absolute z-10 w-full mt-1 bg-n-solid-3 border border-n-weak rounded-lg shadow-lg"
        >
          <!-- Search input -->
          <div class="p-2 border-b border-n-weak">
            <input
              v-model="contactSearch"
              type="text"
              :placeholder="t('CONTACT_PANEL.SEARCH_PLACEHOLDER')"
              class="w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
            />
          </div>
          <div class="max-h-48 overflow-y-auto">
            <button
              type="button"
              class="w-full px-3 py-2 text-left text-n-slate-11 hover:bg-n-alpha-3"
              @click="
                formData.contact_id = null;
                showContactDropdown = false;
              "
            >
              {{ t('DEALS.FORM.NO_CONTACT') }}
            </button>
            <button
              v-for="contact in filteredContacts"
              :key="contact.id"
              type="button"
              class="w-full px-3 py-2 text-left text-n-slate-12 hover:bg-n-alpha-3 flex items-center gap-2"
              @click="selectContact(contact)"
            >
              <span
                class="i-lucide-user size-4 text-n-slate-11 flex-shrink-0"
              />
              <div class="flex flex-col min-w-0">
                <span class="truncate">{{ contact.name }}</span>
                <span
                  v-if="contact.email || contact.phone_number"
                  class="text-xs text-n-slate-10 truncate"
                >
                  {{ contact.email || contact.phone_number }}
                </span>
              </div>
            </button>
            <div
              v-if="filteredContacts.length === 0"
              class="px-3 py-2 text-sm text-n-slate-11 text-center"
            >
              {{ t('CONTACT_PANEL.NO_CONTACTS') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Empresa (Opcional) -->
      <div class="relative">
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('DEALS.FORM.COMPANY') }}
          <span class="text-n-slate-10 text-xs font-normal ml-1">
            ({{ t('DEALS.FORM.OPTIONAL') }})
          </span>
        </label>
        <div class="flex gap-2">
          <button
            type="button"
            class="flex-1 px-3 py-2 text-left border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12 hover:bg-n-alpha-3"
            @click="showCompanyDropdown = !showCompanyDropdown"
          >
            <span v-if="selectedCompany" class="flex items-center gap-2">
              <span class="i-lucide-building-2 size-4 text-n-slate-11" />
              {{ selectedCompany.name }}
            </span>
            <span v-else class="text-n-slate-10">
              {{ t('DEALS.FORM.SELECT_COMPANY') }}
            </span>
          </button>
          <button
            type="button"
            class="flex items-center justify-center px-3 py-2 border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-11 hover:bg-n-alpha-3 hover:text-n-slate-12"
            :title="t('DEALS.FORM.CREATE_COMPANY')"
            @click="openCreateCompanyDialog"
          >
            <span class="i-lucide-plus size-4" />
          </button>
        </div>
        <div
          v-if="showCompanyDropdown"
          class="absolute z-10 w-full mt-1 bg-n-solid-3 border border-n-weak rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          <button
            type="button"
            class="w-full px-3 py-2 text-left text-n-slate-11 hover:bg-n-alpha-3"
            @click="
              formData.company_id = null;
              showCompanyDropdown = false;
            "
          >
            {{ t('DEALS.FORM.NO_COMPANY') }}
          </button>
          <button
            v-for="company in companies"
            :key="company.id"
            type="button"
            class="w-full px-3 py-2 text-left text-n-slate-12 hover:bg-n-alpha-3 flex items-center gap-2"
            @click="selectCompany(company)"
          >
            <span class="i-lucide-building-2 size-4 text-n-slate-11" />
            {{ company.name }}
          </button>
        </div>
      </div>

      <!-- Descrição -->
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('DEALS.FORM.DESCRIPTION') }}
        </label>
        <textarea
          v-model="formData.description"
          rows="3"
          :placeholder="t('DEALS.FORM.DESCRIPTION_PLACEHOLDER')"
          class="w-full px-3 py-2 border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="faded"
          color="slate"
          type="button"
          @click="closeDialog"
        />
        <Button
          type="submit"
          :label="t('DEALS.CREATE.SUBMIT')"
          color="blue"
          :disabled="!isFormValid"
          :is-loading="isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </template>
  </Dialog>

  <!-- Create Contact Dialog -->
  <CreateNewContactDialog
    ref="createContactDialogRef"
    @create="handleContactCreated"
  />

  <!-- Create Company Dialog -->
  <CreateNewCompanyDialog
    ref="createCompanyDialogRef"
    @create="handleCompanyCreated"
  />
</template>
